// The data layer: one thin wrapper per thing a screen needs.
//
// This used to be 1,600 lines of query authorship — OData filter strings,
// 25-item OR-list chunking, skip-token paging, `$select` column lists, and a
// result envelope to unwrap. All of it is gone: the API takes named, typed
// parameters, pages with opaque cursors, and returns rows or throws. What is
// left here is naming and shaping, so screens read the same as before.
//
// Errors are `ApiError` (src/api/errors.ts) and are allowed to propagate —
// screens already render `error.message`, and the API guarantees that text is
// human-readable and free of protected health information.
import { MAX_PAGE_LIMIT } from '@ap/shared'
import { getClient } from '../api/client'
import { recordBulkTouches } from './viewSessions'
import { ApiError } from '../api/errors'
import type {
  Code,
  Page,
  AnalyticsByDimension,
  AnalyticsCorrections,
  AnalyticsKpis,
  AnalyticsSpendByCode,
  AnalyticsParams,
  AnalyticsSpendByEntity,
  Assignment,
  AssignmentRequest,
  AssignmentResult,
  AssociatedEntity,
  InvoiceLineItem,
  Company,
  CompanyEmailDomain,
  IntakeSender,
  SenderFormatDefault,
  InAppUploadBlock,
  SenderRule,
  SenderRulePut,
  Conversation,
  DecisionBatchRequest,
  DecisionBatchResult,
  DecisionRequest,
  DecisionResult,
  DirectoryUser,
  ExportDetail,
  ExportResult,
  FiscalPick,
  Flag,
  Hold,
  IntakeSaveRequest,
  RoutingPreview,
  RoutingPreviewRequest,
  InvoiceListParams,
  IntakeSaveResult,
  Invoice,
  InvoiceBundle,
  InvoiceInclude,
  ReminderSubscription,
  ReminderWrite,
  StuckNode,
  StuckNodeCompany,
  Upload,
  UserRole,
  UserRoleToken,
  Vehicle,
  VehicleImportPreview,
  VehicleImportResult,
  Vendor,
  VendorAlias,
  CompanyAlias,
  CodingRule,
  GroupingRule,
  GroupingRuleCreateRequest,
  GroupingRulePatchRequest,
  LineGroup,
  LineGroupOperation,
  LineGroupsResult,
  VendorExtractionGuidance,
  VendorImportPreview,
  VendorImportResult,
  Department,
  Account,
  InactiveCascade,
  LineItemDetailKind,
  MarketingCategory,
  CreateReportResponse,
  ReportCriteria,
  ReportContentsResponse,
  ReportRequestSummary,
  OversightRule,
  OversightRuleConditionWrite,
  RequiredApprovals,
  UploadNotificationRecipient,
  Notification,
  NotificationSummary,
  UploadNotificationRecipientCreate,
  DecisionSource,
} from '../api/types'
import { decomposeCodeId } from './codeId'
import { filterToParams, type InvoiceFilter } from './filters'
import { CAPITAL_EQUIPMENT_DEPARTMENT_ID, capitalEquipmentAllowed, flagLabel, type InvoiceStatus } from './constants'

const api = () => getClient()

// ---- Invoices ---------------------------------------------------------------

/** Every invoice at one of these statuses, oldest-first — the queue read. Every
    page, not the first 200 (code review D-02, 2026-10-17): a backlog past one
    page used to drop its newest invoices with nothing on screen to say so. */
export async function loadInvoicesByStatus(statuses: readonly string[]): Promise<Invoice[]> {
  return collectPages((cursor) =>
    api().listInvoices({
      status: statuses as InvoiceStatus[],
      sort: 'invoice_date_asc',
      limit: 200,
      cursor,
    }),
  )
}

export async function getInvoice(invoiceId: string): Promise<Invoice> {
  const bundle = await api().getInvoice(invoiceId)
  return bundle.invoice
}

/** The detail modal's one round trip: header plus whichever satellites it shows. */
export function getInvoiceBundle(invoiceId: string, include: InvoiceInclude[]): Promise<InvoiceBundle> {
  return api().getInvoice(invoiceId, include)
}

interface ArchivePage {
  rows: Invoice[]
  /** Opaque — pass it back for the next page; never inspect it. */
  cursor?: string
}

/** Archives pages server-side, and filters there too, so filtering and paging
    compose. The client passes named parameters; it never composes a query. */
export async function loadArchivePage(
  statuses: readonly string[],
  filter: InvoiceFilter,
  cursor?: string,
  pageSize = 50,
): Promise<ArchivePage> {
  const page = await api().listInvoices({
    ...filterToParams(filter),
    status: statuses as InvoiceStatus[],
    sort: 'invoice_date_desc',
    limit: pageSize,
    cursor,
  })
  return { rows: page.data, cursor: page.next_cursor ?? undefined }
}

/**
 * One page of invoices for the DASHBOARD's listing (owner, 2026-09-14).
 *
 * Separate from `loadArchivePage` because the two take different filters: that
 * one takes the queue screens' `InvoiceFilter`, this one takes the Reporting
 * criteria the Filters button produces (`lib/criteria.ts`). It asks for the
 * TOTAL as well as the page, so the listing can say how many invoices are in
 * this view rather than only how many are on screen.
 *
 * NO STATUS OF ITS OWN: "all applicable invoices" means every status the filter
 * did not exclude, archived ones included.
 */
export async function loadDashboardInvoices(
  params: InvoiceListParams,
  cursor?: string,
  pageSize = 25,
): Promise<{ rows: Invoice[]; cursor?: string; total?: number }> {
  const page = await api().listInvoices({
    ...params,
    sort: 'invoice_date_desc',
    limit: pageSize,
    ...(cursor === undefined ? {} : { cursor }),
  })
  return {
    rows: page.data,
    cursor: page.next_cursor ?? undefined,
    total: page.total,
  }
}

/** The invoices in one export batch at a given status (Closing Confirmation). */
export async function loadInvoicesByExportKey(exportId: string, status: string): Promise<Invoice[]> {
  const page = await api().listInvoices({
    export_id: exportId,
    status: [status as InvoiceStatus],
    limit: 200,
  })
  return page.data
}

/** The Import Control save. One request, one transaction: header fields, line
    upserts and removals, flag auto-resolution, the correction rows, and — with
    `release` — the release gates and the Import Control approve itself. */
export function intakeSave(
  invoiceId: string,
  body: IntakeSaveRequest,
  /** Where the Release was clicked (D-331); only sent with `release: true`. */
  source?: DecisionSource,
): Promise<IntakeSaveResult> {
  return api().intakeSave(invoiceId, body.release && source !== undefined ? { ...body, decision_source: source } : body)
}

/** Which routing rules a release or reinstate would follow (D-347): the form as
    it stands, unsaved edits included. Advisory; the release decides again. */
export function routingPreview(invoiceId: string, body: RoutingPreviewRequest): Promise<RoutingPreview> {
  return api().routingPreview(invoiceId, body)
}

// ---- Line items ----------------------------------------------------------------

export function loadLineItems(invoiceId: string): Promise<InvoiceLineItem[]> {
  return api().listLineItems(invoiceId)
}

/** Lines for many invoices at once. The API takes the id list; the old client
    chopped it into 25-id batches because the request URL overflowed. */
export function loadLineItemsForInvoices(invoiceIds: string[]): Promise<InvoiceLineItem[]> {
  return api().listLineItemsForInvoices(invoiceIds)
}

/**
 * "What is it?" — the half-write, and the only way an approval tier moves the
 * account. The server re-applies the composition rule to the department,
 * so read the returned line rather than assuming the other half held.
 */
export async function applyLineItemAccountEdit(
  invoiceId: string,
  lineItemId: string,
  accountId: string | null,
  rowVersion: number,
): Promise<{ applied: boolean; reason?: string; lineItem?: InvoiceLineItem }> {
  try {
    const res = await api().setLineItemAccount(invoiceId, lineItemId, {
      account_id: accountId,
      row_version: rowVersion,
    })
    return { applied: true, lineItem: res.line_item }
  } catch (e) {
    if (e instanceof ApiError) return { applied: false, reason: e.message }
    throw e
  }
}

/**
 * "Who is it for?" — the half-write, from Department Approval onward. Refused
 * with the server's own wording when the pair does not compose (422) or the
 * step, the hold or the assignment says no.
 */
export async function applyLineItemDepartmentEdit(
  invoiceId: string,
  lineItemId: string,
  departmentId: string | null,
  rowVersion: number,
): Promise<{ applied: boolean; reason?: string; lineItem?: InvoiceLineItem }> {
  try {
    const res = await api().setLineItemDepartment(invoiceId, lineItemId, {
      department_id: departmentId,
      row_version: rowVersion,
    })
    return { applied: true, lineItem: res.line_item }
  } catch (e) {
    if (e instanceof ApiError) return { applied: false, reason: e.message }
    throw e
  }
}

/**
 * The Marketing Category's upsert (owner item 11, 2026-08-19) — §4.10.
 * `rowVersion` is the DETAIL ROW's own version (`0` = believed not to exist
 * yet), never the line item's. Import Control, and wherever the Account is
 * editable (D-190, D-192, `marketingCategoryEditableAt` in `./required.ts`).
 */
export async function applyLineItemMarketingDetailEdit(
  invoiceId: string,
  lineItemId: string,
  categoryId: string | null,
  rowVersion: number,
): Promise<{ applied: boolean; reason?: string; lineItem?: InvoiceLineItem }> {
  try {
    const res = await api().setLineItemMarketingDetail(invoiceId, lineItemId, {
      category_id: categoryId,
      row_version: rowVersion,
    })
    return { applied: true, lineItem: res.line_item }
  } catch (e) {
    if (e instanceof ApiError) return { applied: false, reason: e.message }
    throw e
  }
}

/**
 * The line-item bulk bar's Department options (D-228): the INTERSECTION of `departmentsFor` across the selected
 * lines, because one department has to compose with every one of them.
 *
 * An EMPTY result is a real answer and the control must say so rather than
 * render an empty dropdown — 91% of the possible department × account
 * combinations are not codes, so a mixed selection genuinely often has
 * nothing in common. A line with NO account composes with nothing, so a
 * selection containing one is empty by construction: there is no code to
 * reach from it, and offering any department would be a lie.
 *
 * ITEM 8 — the Capital Equipment floor: dropped from the intersection
 * whenever ANY selected line's own total is under $5,000 (or unknown), so
 * the bar does not offer an option the server will refuse with
 * `422 capital_equipment_minimum`. A CONVENIENCE only — the server is the
 * gate — but offering a choice it will bounce is worse than not offering it.
 */
export async function loadBulkDepartmentOptions(
  lines: readonly { account_id: string | null | undefined; total?: number | null }[],
): Promise<Department[]> {
  if (lines.length === 0) return []
  if (lines.some((line) => !line.account_id)) return []
  const distinct = [...new Set(lines.map((line) => line.account_id as string))]
  const lists = await Promise.all(
    distinct.map((accountId) => api().listDepartments({ active: true, account_id: accountId, limit: MASTER_PAGE_LIMIT })),
  )
  const [first, ...rest] = lists
  if (first === undefined) return []
  const common = new Set(first.map((c) => c.department_id))
  for (const list of rest) {
    const here = new Set(list.map((c) => c.department_id))
    for (const departmentId of [...common]) if (!here.has(departmentId)) common.delete(departmentId)
  }
  if (lines.some((line) => !capitalEquipmentAllowed(line.total))) {
    common.delete(CAPITAL_EQUIPMENT_DEPARTMENT_ID)
  }
  return first.filter((c) => common.has(c.department_id))
}

/**
 * ITEM 14(b) (owner item 14, 2026-08-17) — a CLIENT-SIDE FALLBACK for "is this
 * account Capital-Equipment-only?", used to explain rather than hide the
 * "Who is it for?" picker when it would otherwise render with zero options.
 *
 * `190110` is the ONLY department `88-880`/`88-885` compose with, so under
 * the $5,000 floor `searchDepartments` drops it and the picker offers
 * nothing — the owner's literal complaint. Hard-coding those two account ids
 * is exactly what NOT to do: they are chart-derived, and one new
 * `XXXXXX-88-880` row would make both non-capital while this file kept
 * insisting otherwise. So this asks the chart itself, the same question
 * `isCapitalEquipmentAccount` (`backend/src/domain/codeComposition.ts`)
 * answers server-side: does this account's department list reduce to
 * exactly `['190110']`?
 *
 * MUST call the RAW `listDepartments`, never `searchDepartments` — that
 * helper already drops `190110` from its results once a total is passed, so
 * every capital-equipment account would read as "no options" and this
 * function could never tell "capital-only" apart from "the floor already
 * filtered everything". Memoized per account id: the chart is small
 * and static (88 active accounts), so this is at most 88 API calls a session,
 * each made once.
 */
const capitalOnlyAccountCache = new Map<string, Promise<boolean>>()

export function isCapitalEquipmentOnlyAccount(accountId: string): Promise<boolean> {
  let cached = capitalOnlyAccountCache.get(accountId)
  if (cached === undefined) {
    cached = api()
      .listDepartments({ active: true, account_id: accountId, limit: MASTER_PAGE_LIMIT })
      .then((results) => {
        const departmentIds = results.map((d) => d.department_id)
        return departmentIds.length === 1 && departmentIds[0] === CAPITAL_EQUIPMENT_DEPARTMENT_ID
      })
    capitalOnlyAccountCache.set(accountId, cached)
  }
  return cached
}

// ---- Flags ------------------------------------------------------------------

/** A queue row already carries its unresolved flag types, so the chips need no
    second read — this is the accessor, not a fetch. */
export function unresolvedFlagTypes(invoice: Invoice): string[] {
  return invoice.flag_types ?? []
}

/** The same flags' labels, in the same order. The server sends each one's
    `flag_definitions` label beside its type (`flag_labels`), so a RETIRED flag
    (`GLCodeLineMismatch`, D-401), which is in no live copy here, still reads by
    its label: stopping a flag must not change how an existing one looks. */
export function unresolvedFlagLabels(invoice: Invoice): string[] {
  return unresolvedFlagTypes(invoice).map((t, i) => flagLabel(t, invoice.flag_labels?.[i]))
}

/** "Mark Resolved". Deliberately writes no correction row, so Required
    Corrections Per Flag counts only the flags a person actually corrected. */
export function resolveFlag(
  invoiceId: string,
  flagId: string,
): Promise<{ flag: Flag; unresolved_flag_count: number }> {
  return api().resolveFlag(invoiceId, flagId)
}

// ---- Decisions --------------------------------------------------------------

/** Every status transition. Synchronous: the response carries the new status,
    so there is nothing to poll and nothing to guess. A refusal throws with the
    reason (`Blocked by an active hold`, `Out of order — record status is …`,
    `Not authorized for this step`) and writes nothing at all. */
export function createDecision(body: DecisionRequest): Promise<DecisionResult> {
  return api().createDecision(body)
}

/** Bulk approve / deny / Confirm Batch. Always resolves: each invoice is its
    own transaction and each result says whether it applied and why not.

    Also files a zero-second handling-time touch per APPLIED invoice, so bulk
    work shows up in the median as fast rather than as absent (`viewSessions.ts`).
    Refused items are left out: the reviewer did spend time on them, but a
    refusal is not a handling of the invoice, and counting it would credit the
    step with work that changed nothing. */
export async function createDecisionBatch(body: DecisionBatchRequest): Promise<DecisionBatchResult> {
  const result = await api().createDecisionBatch(body)
  recordBulkTouches(
    result.results.filter((item) => item.applied).map((item) => item.invoice_id),
    body.step,
  )
  return result
}

// ---- Holds ------------------------------------------------------------------

/** Place a hold. The category is required and the reason only under `other`
    (owner, 2026-09-25) — the server refuses otherwise. */
export function createHold(invoiceId: string, reasonCategory: string, reason?: string): Promise<Hold> {
  const trimmed = reason?.trim()
  return api().createHold(invoiceId, {
    reason_category: reasonCategory,
    ...(trimmed ? { reason: trimmed } : {}),
  })
}

export function releaseHold(holdId: string): Promise<Hold> {
  return api().releaseHold(holdId)
}

// ---- Comment thread ---------------------------------------------------------

export function loadConversations(invoiceId: string): Promise<Conversation[]> {
  return api().listConversations(invoiceId)
}

/** The server stamps the author and the record's company, so a comment's
    visibility mirrors its record without the client naming anything. */
export function createConversation(
  invoiceId: string,
  body: string,
  mentions: string[],
): Promise<{ conversation: Conversation; notifications_queued: number }> {
  return api().createConversation(invoiceId, { body, mentions })
}

// ---- Associated entities ----------------------------------------------------

export function loadAssociatedEntities(invoiceId: string): Promise<AssociatedEntity[]> {
  return api().listAssociatedEntities(invoiceId)
}

export function createAssociatedEntity(
  invoiceId: string,
  record: Partial<AssociatedEntity>,
): Promise<AssociatedEntity> {
  return api().createAssociatedEntity(invoiceId, record)
}

export function updateAssociatedEntity(
  id: string,
  changes: Partial<AssociatedEntity>,
): Promise<AssociatedEntity> {
  return api().updateAssociatedEntity(id, changes)
}

export function deactivateAssociatedEntity(id: string, rowVersion: number): Promise<void> {
  return api().deleteAssociatedEntity(id, rowVersion)
}

// ---- Uploads ----------------------------------------------------------------

export function getUpload(uploadId: string): Promise<Upload> {
  return api().getUpload(uploadId)
}

/** A short-lived signed URL for the source document. The endpoint checks the
    caller's own document access before issuing one — that check is the only
    gate now, so it is load-bearing. */
// ---- Reports (Analytics -> Reporting, owner 2026-09-10) -------------------

/**
 * Run a report. The server selects the invoices, records the request, and hands
 * back each one with the page span its source pages occupy inside its upload
 * packet — NOT a PDF. `lib/reportPdf.ts` assembles that from these rows.
 *
 * Throws `report_too_large` (422) above `REPORT_INVOICE_CAP`. Zero matches is a
 * success with `invoice_count: 0`.
 */
export function createReport(criteria: ReportCriteria): Promise<CreateReportResponse> {
  return api().createReport(criteria)
}

/** The most recent report requests this caller may see — the audit list. */
export function listReports(): Promise<ReportRequestSummary[]> {
  return api().listReports()
}

/**
 * A recorded report's invoices, fetched again by the ids it stored (owner,
 * 2026-09-14) — what the listing's **Download** button runs.
 *
 * The SET is what was pinned; the values are today's. See `docs/decisions.md`
 * D-99 for why there is no snapshot to reproduce instead.
 */
export function getReportContents(reportId: string): Promise<ReportContentsResponse> {
  return api().getReportContents(reportId)
}

export function getUploadPdfUrl(
  uploadId: string,
): Promise<{ url: string; expires_at: string; file_name?: string | null }> {
  return api().getUploadPdfUrl(uploadId)
}

/** Uploads one file: ask for a place to put it, put it there, then register it.
    The registration key is required and idempotent, so a retry cannot create a
    second row or a second extraction charge. */
/**
 * `selectedCompanyId` is the uploader's company pick (owner item 21,
 * 2026-08-31) — ONE value for a whole batch, so the caller passes the same one
 * for every file. Sent on BOTH calls: the presign so a bad or out-of-scope
 * choice fails before the bytes go up, the register because that is the call
 * that stores it. The server validates it against the caller's own company
 * scope either way; this layer is a courier, not a gate.
 */
export async function uploadDocument(
  file: File,
  selectedCompanyId?: string | null,
): Promise<Upload> {
  const client = api()
  const companyField =
    selectedCompanyId === undefined || selectedCompanyId === null || selectedCompanyId === ''
      ? {}
      : { selected_company_id: selectedCompanyId }
  const presigned = await client.presignUpload({
    file_name: file.name,
    content_type: file.type || 'application/pdf',
    byte_size: file.size,
    expected_type: 'invoice',
    ...companyField,
  })
  await client.putUploadFile(presigned, file)
  return client.registerUpload(
    {
      upload_id: presigned.upload_id,
      object_key: presigned.object_key,
      expected_type: 'invoice',
      ...companyField,
    },
    presigned.upload_id,
  )
}

// ---- Exports ----------------------------------------------------------------

/** Approve & Export. One call builds every per-company file, writes the copy of
    record, stamps the batch on each invoice and writes each Export Control
    approval — or fails and writes nothing. The file contents are the server's:
    Point Click Care parses those bytes and the format is immutable.

    `pick` is the controller's choice beside the company's Export button: the
    year goes to column 4 and the period (1-12) to column 5 of every row (owner,
    2026-09-23, D-137 and D-138). Neither is read off the invoice. */
export function createExport(
  invoiceIds: string[],
  pick: FiscalPick,
  source?: DecisionSource,
): Promise<ExportResult> {
  return api().createExport(invoiceIds, pick, undefined, source)
}

export function getExport(exportId: string): Promise<ExportDetail> {
  return api().getExport(exportId)
}

export function getExportFileUrl(
  exportId: string,
  fileId: string,
): Promise<{ url: string; file_name?: string | null }> {
  return api().getExportFileUrl(exportId, fileId)
}

// ---- Reminder subscriptions -------------------------------------------------

export function loadMyReminders(): Promise<ReminderSubscription[]> {
  return api().listReminderSubscriptions()
}

export function createReminder(record: ReminderWrite): Promise<ReminderSubscription> {
  return api().createReminderSubscription(record)
}

/** Editing is supersede-then-create, in one server transaction, carrying the
    last-sent stamp forward so a mid-day edit cannot double-send. */
export function replaceReminder(
  id: string,
  record: ReminderWrite & { row_version: number },
): Promise<{ subscription: ReminderSubscription; superseded_id: string }> {
  return api().replaceReminderSubscription(id, record)
}

export function setReminderStatus(
  id: string,
  status: 'active' | 'paused' | 'cancelled',
): Promise<ReminderSubscription> {
  return api().setReminderSubscriptionStatus(id, status)
}

// ---- Assignments ------------------------------------------------------------

/** EVERY PAGE, not the first 200 rows (owner, 2026-10-14). The list is the
    whole history, newest first, so one page left the oldest live seats off the
    screen once dev held more than 200 rows — and a person adding someone who
    already held one of those seats saw the server's refusal as an error. */
export async function listAssignments(): Promise<Assignment[]> {
  return collectPages((cursor) => api().listAssignments({ workspace: 'invoice', limit: 200, cursor }))
}

/** Appoint or retire. Synchronous, and the row is written already applied —
    there is no pending request to poll for. */
export function createAssignment(body: AssignmentRequest): Promise<AssignmentResult> {
  return api().createAssignment(body)
}

// ---- Internal step roles ----------------------------------------------------

export function listUserRoles(): Promise<UserRole[]> {
  return api().listUserRoles({ workspace: 'invoice' })
}

export function grantUserRole(body: {
  role: UserRoleToken
  user_id: string
  user_upn: string
  user_name?: string | null
  /** The directory mail address when Graph returns one; the server falls back
      to the UPN, which is what mentions and digests then compare against. */
  user_email?: string | null
}): Promise<UserRole> {
  return api().createUserRole({ workspace: 'invoice', ...body })
}

export function revokeUserRole(userRoleId: string): Promise<void> {
  return api().deleteUserRole(userRoleId)
}

// ---- Pickers ----------------------------------------------------------------
// Every picker searches server-side now. The old client loaded the whole chart
// and the whole fleet into memory and filtered there, because the
// Dataverse data source silently ignored a `$filter` on those columns.

/** Option shape the shared EntityPicker consumes (id + display text). */
interface PickerOption {
  id: string
  primary: string
  secondary?: string
}

export async function searchVendors(term: string, top = 50): Promise<Vendor[]> {
  const page = await api().listVendors({ q: term || undefined, active: true, limit: top })
  return page.data
}

/**
 * Whether vendor `vendorId` is Inactive (code review G-09, 2026-10-17). The
 * Vendor picker offers Active vendors only, so only the vendor an invoice
 * already holds can be Inactive; this asks the server about that one. The
 * search ranks an exact ID first.
 */
export async function vendorIsInactive(vendorId: string): Promise<boolean> {
  const page = await api().listVendors({ q: vendorId, active: false, limit: 50 })
  return page.data.some((v) => v.vendor_id === vendorId && v.is_active === false)
}

export async function searchCompanies(term: string, top = 100): Promise<Company[]> {
  const page = await api().listCompanies({ q: term || undefined, active: true, limit: top })
  return page.data
}

/** "What is it?" — the picker every coding surface offers.
 *
 * `limit: MASTER_PAGE_LIMIT`, same reasoning as `loadAccounts` below
 * (`data.ts:894-901`): there are 88 active accounts and the API's
 * default page is 50, so a blank search already dropped 32 of them before a
 * user typed anything narrow enough to surface one by substring. */
export function searchAccounts(term: string): Promise<Account[]> {
  return api().listAccounts({ q: term || undefined, active: true, limit: MASTER_PAGE_LIMIT })
}

/** The Marketing sub-form's Category picker (owner item 11, 2026-08-19). The
    vocabulary is small (three seeded rows today), but capped the same way as
    every other master read, on the same reasoning. */
export function searchMarketingCategories(term: string): Promise<MarketingCategory[]> {
  return api().listMarketingCategories({ q: term || undefined, active: true, limit: MASTER_PAGE_LIMIT })
}

/**
 * "Who is it for?" — ONE-DIRECTIONAL: the options derive from the account,
 * never the reverse. Passing no account offers every active department,
 * which is only right where no account is settled yet.
 *
 * ITEM 8 — the Capital Equipment floor: `total` is OPTIONAL and deliberately
 * so. Passing a LINE's own total (the per-line "Who is it for?" picker on an
 * actual invoice) drops `190110` from the results when that line is under
 * the floor, same reasoning as `loadBulkDepartmentOptions` (C15) — a
 * convenience so the picker does not offer an option the server will
 * refuse. Passing NOTHING (the Rules screen's department picker, used to
 * author a standing vendor coding rule) applies no filter at all: a rule is
 * written before any invoice's amount exists, so the floor has nothing to
 * check yet and must not narrow what a rule may name.
 */
export async function searchDepartments(
  term: string,
  accountId?: string,
  total?: number | null,
): Promise<Department[]> {
  // The 42 active departments fit under the API's default page of 50, so this
  // does not truncate today. The explicit limit keeps it that way if the master
  // grows, for the same reason as `searchAccounts` above.
  const results = await api().listDepartments({
    q: term || undefined,
    active: true,
    limit: MASTER_PAGE_LIMIT,
    ...(accountId === undefined ? {} : { account_id: accountId }),
  })
  if (total === undefined || capitalEquipmentAllowed(total)) return results
  return results.filter((c) => c.department_id !== CAPITAL_EQUIPMENT_DEPARTMENT_ID)
}

// ---- Half labels ------------------------------------------------------------
//
// A line item carries each half as an ID — `67-502`, `689359` — never its
// name. The name is master data, so every surface that shows a half has to resolve it, and a
// surface that does not shows an empty box on a line that is in fact coded.
//
// Both masters are small and static (88 active accounts, 42 active departments), so this
// reads each once per session and memoizes the promise. Two deliberate choices:
//
//  - `limit: MASTER_PAGE_LIMIT`, because the API's default page is 50 and there
//    are 88 active accounts. A default page would silently drop nearly half of them and
//    the symptom would be an empty box on exactly the lines nobody looked at.
//  - NO `active` filter, unlike the pickers. A line may hold a half that was
//    retired under it; that line still has to render and still has to export,
//    the same reason the server's name-repopulation runs with `activeOnly:
//    false`. Offering a retired half is the picker's problem, not the label's.

/** The API's largest page, `MAX_PAGE_LIMIT`. */
const MASTER_PAGE_LIMIT = MAX_PAGE_LIMIT

let accountLabelsOnce: Promise<ReadonlyMap<string, string>> | null = null
let departmentLabelsOnce: Promise<ReadonlyMap<string, string>> | null = null
let accountDetailKindsOnce: Promise<ReadonlyMap<string, LineItemDetailKind>> | null = null
let inactiveAccountIdsOnce: Promise<ReadonlySet<string>> | null = null

/** Account id -> label, for display. Read once, memoized. */
export function loadAccountLabels(): Promise<ReadonlyMap<string, string>> {
  accountLabelsOnce ??= api()
    .listAccounts({ limit: MASTER_PAGE_LIMIT })
    .then((rows) => new Map(rows.map((a) => [a.account_id, a.account_name])))
    .catch((e: unknown) => {
      // A failed read must not poison the cache for the rest of the session.
      accountLabelsOnce = null
      throw e
    })
  return accountLabelsOnce
}

/** Department id -> label, for display. Read once, memoized. */
export function loadDepartmentLabels(): Promise<ReadonlyMap<string, string>> {
  departmentLabelsOnce ??= api()
    .listDepartments({ limit: MASTER_PAGE_LIMIT })
    .then((rows) => new Map(rows.map((d) => [d.department_id, d.department_name])))
    .catch((e: unknown) => {
      departmentLabelsOnce = null
      throw e
    })
  return departmentLabelsOnce
}

/**
 * Account id -> `detail_kind` (owner item 11, 2026-08-19) — THE MARKETING
 * TRIGGER for a line's Additional Detail (D-154; the Transportation section is
 * the line's source, `additionalDetailSections` in `@ap/shared`, D-381).
 * A row absent from the map (or a `null`/missing `detail_kind`) means "no
 * Marketing Category" — this never carries a hard-coded list of account ids, only
 * whatever `GET /accounts` reports. Read once, memoized, same shape as
 * the two label caches above.
 */
export function loadAccountDetailKinds(): Promise<ReadonlyMap<string, LineItemDetailKind>> {
  accountDetailKindsOnce ??= api()
    .listAccounts({ limit: MASTER_PAGE_LIMIT })
    .then((rows) => new Map(rows.filter((e) => !!e.detail_kind).map((e) => [e.account_id, e.detail_kind as LineItemDetailKind])))
    .catch((e: unknown) => {
      accountDetailKindsOnce = null
      throw e
    })
  return accountDetailKindsOnce
}

/**
 * The Accounts that are Inactive (owner, 2026-09-25 — D-175): what lets a line
 * holding one with no Department yet show a red Account and a reason on the
 * disabled Approve. Read once, memoized, same shape as the caches above, and
 * dropped with them by every half write.
 */
export function loadInactiveAccountIds(): Promise<ReadonlySet<string>> {
  inactiveAccountIdsOnce ??= api()
    .listAccounts({ limit: MASTER_PAGE_LIMIT })
    .then((rows) => new Set(rows.filter((a) => a.is_active === false).map((a) => a.account_id)))
    .catch((e: unknown) => {
      inactiveAccountIdsOnce = null
      throw e
    })
  return inactiveAccountIdsOnce
}

/** Drop every cache here. Every half write does, and the mock harness reseeds between tests. */
export function resetHalfLabelCaches(): void {
  accountLabelsOnce = null
  departmentLabelsOnce = null
  accountDetailKindsOnce = null
  inactiveAccountIdsOnce = null
}

/** Vehicles: the option id is the vehicles-row UUID, because that is what a
    `Vehicle` tag stores — `associated_entities.vehicle_id` is a foreign key to
    `vehicles.id` (api-contract §9), not the fleet number. The number is the
    DISPLAY value, and it is what the row's `entity_name` keeps. Sending the
    number as the id is the M9 defect: the API validates `vehicle_id` as a uuid,
    so the pick was refused or dropped and the tag saved unresolved. */
export async function searchVehicles(term: string, top = 50): Promise<PickerOption[]> {
  const page = await api().listVehicles({ q: term || undefined, active: true, limit: top })
  return page.data.map((v) => ({
    id: v.id,
    primary: v.number,
    secondary: [v.make, v.model, v.license_plate, v.vin].filter(Boolean).join(' · ') || undefined,
  }))
}

/** Staff: the id is the user principal name, because that is what entity
    resolution writes. The search is Microsoft Graph on the signed-in user's own
    token — the same directory the old connector read, and none of it is PHI. */
export async function searchStaff(term: string, top = 25): Promise<PickerOption[]> {
  const users = await api().searchDirectoryUsers(term, top)
  return users.map((u) => ({
    id: u.upn,
    primary: u.display_name || u.upn,
    secondary: [u.job_title, u.department].filter(Boolean).join(' · ') || u.mail || undefined,
  }))
}

/** People to appoint. Same directory search; an appointment carries the
    directory id and the user principal name. */
export function searchPeople(term: string, top = 25): Promise<DirectoryUser[]> {
  return api().searchDirectoryUsers(term, top)
}

// ---- Reference (master) data ------------------------------------------------
// Retire and reactivate are one PATCH of `is_active`, not separate verbs. A
// retired row persists as audit and drops out of every picker.

export async function loadVendorsForManagement(
  term?: string,
  limit = 50,
  options: { errantZip?: boolean } = {},
): Promise<Vendor[]> {
  const page = await api().listVendors({ q: term || undefined, limit, errant_zip: options.errantZip || undefined })
  return page.data
}

/** The ACTIVE vendors with an errant ZIP (D-241), for the References → Vendors
    warning: the first page, by name, and the whole count. Asked of the server
    because the tab loads only part of the master. */
export async function loadErrantZipVendors(): Promise<{ vendors: Vendor[]; total: number }> {
  const page = await api().listVendors({ errant_zip: true, active: true, limit: 200 })
  return { vendors: page.data, total: page.total ?? page.data.length }
}

export function createVendor(record: Partial<Vendor> & { vendor_id: string; vendor_name: string }): Promise<Vendor> {
  return api().createVendor(record)
}

// `vendorId` is the BUSINESS KEY (`vendor_id`), never the internal uuid `id` —
// the route and the mock both look the row up by it.
export function updateVendor(
  vendorId: string,
  changes: Partial<Vendor> & { row_version: number },
): Promise<Vendor> {
  return api().updateVendor(vendorId, changes)
}

/** The Point Click Care vendor list is parsed on the server (owner item 12),
    so preview and commit cannot disagree about what the file said. Matched on
    `vendor_id` alone, never `vendor_name` (95 real duplicate-name groups
    exist). Absent vendors are MARKED INACTIVE on commit (owner ruling,
    2026-08-28) — the list is the whole vendor list — so the preview's
    `absent_total` is a headline count, not a footnote. */
export function previewVendorImport(file: File): Promise<VendorImportPreview> {
  return api().previewVendorImport(file)
}

export function commitVendorImport(previewToken: string): Promise<VendorImportResult> {
  return api().commitVendorImport(previewToken)
}

// `vendorId` is the BUSINESS KEY (`vendor_id`), never the internal uuid `id`.
export function setVendorActive(vendorId: string, isActive: boolean, rowVersion: number): Promise<Vendor> {
  return api().updateVendor(vendorId, { is_active: isActive, row_version: rowVersion })
}

export async function loadCompaniesForManagement(term?: string): Promise<Company[]> {
  const page = await api().listCompanies({ q: term || undefined, limit: 200 })
  return page.data
}

export function createCompany(record: { company_id: string; company_name: string }): Promise<Company> {
  return api().createCompany(record)
}

/** A company may be renamed. The name captured onto an invoice at match time
    stays frozen as the accounting record and is never re-synced — authority and
    visibility key on the company id, so a rename moves nothing.
    `companyId` is the BUSINESS KEY (`company_id`), never the internal uuid `id`. */
export function updateCompany(
  companyId: string,
  changes: { company_name?: string; is_active?: boolean; row_version: number },
): Promise<Company> {
  return api().updateCompany(companyId, changes)
}

/**
 * Every matching page, in order, for a caller who wants the whole collection
 * rather than one server page. Stops on `next_cursor === null`; hard-stops at
 * 50 pages (10,000 rows at the max page size) so a broken or forged cursor
 * cannot spin forever. Mirrors `collectAll` in `backend/src/lambda/match.ts`,
 * which reads the same two reference masters the same way.
 */
async function collectPages<T>(read: (cursor?: string) => Promise<Page<T>>): Promise<T[]> {
  const rows: T[] = []
  let cursor: string | undefined
  for (let page = 0; page < 50; page++) {
    const res = await read(cursor)
    rows.push(...res.data)
    if (!res.next_cursor) return rows
    cursor = res.next_cursor
  }
  return rows
}

/** Both `company_email_domains` and `intake_senders` page at the server's
    default of 50 (docs/api.md §15), same as every other reference collection —
    this exhausts the cursor so the Senders tab sees every row rather than
    however many fit on one page. The return type stays an array: every
    existing caller (`CompanyDialog`, `SendersManager`, and the tests) reads a
    plain list and none of them needs to page it by hand. */
export async function loadCompanyEmailDomains(companyId?: string): Promise<CompanyEmailDomain[]> {
  return collectPages((cursor) =>
    api().listCompanyEmailDomains({ company_id: companyId, limit: MASTER_PAGE_LIMIT, cursor }),
  )
}

export async function loadIntakeSenders(): Promise<IntakeSender[]> {
  return collectPages((cursor) => api().listIntakeSenders({ limit: MASTER_PAGE_LIMIT, cursor }))
}

/** Every uploader rule's format default, Active and Inactive (D-379). */
export async function loadSenderFormatDefaults(): Promise<SenderFormatDefault[]> {
  return collectPages((cursor) => api().listSenderFormatDefaults({ limit: MASTER_PAGE_LIMIT, cursor }))
}

/** Every uploader rule's in-app upload block, Active and Inactive (D-394). */
export async function loadInAppUploadBlocks(): Promise<InAppUploadBlock[]> {
  return collectPages((cursor) => api().listInAppUploadBlocks({ limit: MASTER_PAGE_LIMIT, cursor }))
}

/** One Rules → Companies → Uploaders rule, every row of it in ONE server
    transaction (`PUT /sender-rules`, code review G-01). `lib/senders.ts`
    builds the body. */
export function saveSenderRule(body: SenderRulePut): Promise<{ sender_rule: SenderRule; changed: boolean }> {
  return api().putSenderRule(body)
}

/** The WHOLE chart (269 rows, 167 of them active), not one page. A single
    `limit: 200` page silently drops the last 69, which is a wrong answer, not
    a short list; the old one-page reader (`loadCodesForManagement`, deleted in
    code review F-05) did exactly that. Every reader of the chart uses this. */
export async function loadAllCodes(): Promise<Code[]> {
  return collectPages((cursor) => api().listCodes({ limit: MASTER_PAGE_LIMIT, cursor }))
}

/**
 * `department_name` / `account_name` (owner item 5, 2026-08-19) name a
 * half being CREATED by this write — the composed Add Reference dialog's own
 * pickers, not free text. The server applies one only when that half does not
 * already exist, so an existing half's authored label is never overwritten;
 * omit either and that half falls back to its provisional label, exactly as
 * before these fields existed.
 */
/**
 * WRITING A HALF DROPS THE LABEL CACHES (QA wave B1, 2026-09-22, B1-04).
 *
 * `loadAccountLabels` and `loadDepartmentLabels` memoize for the session,
 * which is right for masters nothing changes — and these five endpoints are
 * exactly the ones that do. A half created or renamed after the maps were read
 * was absent from them, so every surface that shows a half printed its ID:
 * a coding rule read "default that line item's Account to 99-999", and the
 * Rules tab's own search, which matches on the label, could not find the rule
 * it had just created.
 */
function dropLabelCachesAfter<T>(write: Promise<T>): Promise<T> {
  return write.then((row) => {
    resetHalfLabelCaches()
    return row
  })
}

export function createCode(record: {
  code_id: string
  code_name: string
  department_name?: string
  account_name?: string
}): Promise<Code> {
  return dropLabelCachesAfter(api().createCode(record))
}

// `codeId` is the BUSINESS KEY (`code_id`), never the internal uuid `id` —
// the route and the mock both look the row up by it.
export function updateCode(
  codeId: string,
  changes: { code_name?: string; is_active?: boolean; row_version: number },
): Promise<Code> {
  return api().updateCode(codeId, changes)
}

/**
 * The two halves of a code.
 *
 * SEEDED as a derivation of the chart, but authorable in their own right since
 * owner item 10, 2026-08-31 — see `createDepartment` / `createAccount`
 * below and `docs/decisions.md` D-69.
 */
export function loadDepartments(
  params: { q?: string; account_id?: string } = {},
): Promise<Department[]> {
  return api().listDepartments({ ...params, active: true })
}

/** `limit: MASTER_PAGE_LIMIT` because the API's default page is 50 and there are
    88 active accounts. Its one caller counts the list — the coding-rule dialog's
    reach warning, "this department reaches N of <total> accounts" — so a
    default page did not truncate a picker, it printed a WRONG DENOMINATOR: N of
    50. The harness runs the real route, which pages the same way. Label lookups belong to `loadAccountLabels`
    above, which is memoized and unfiltered; this one is `active`-only. */
export function loadAccounts(params: { q?: string } = {}): Promise<Account[]> {
  return api().listAccounts({ ...params, active: true, limit: MASTER_PAGE_LIMIT })
}

/**
 * THE WHOLE half master for the References table's Accounts / Departments
 * views — retired rows included, so "Show retired" has something to show.
 * `loadDepartments` / `loadAccounts` above are the PICKERS and stay
 * `active`-only; a management list is not a picker.
 */
export function loadAllDepartments(): Promise<Department[]> {
  return api().listDepartments({ limit: MASTER_PAGE_LIMIT })
}

export function loadAllAccounts(): Promise<Account[]> {
  return api().listAccounts({ limit: MASTER_PAGE_LIMIT })
}

/**
 * Owner item 10, 2026-08-31 (D-69) — a Department authored on its own, ahead of
 * any code that uses it. The server stamps `origin: 'standalone'`, which is
 * what stops code retirement from quietly retiring it later.
 */
export function createDepartment(record: {
  department_id: string
  department_name: string
  search_terms?: string | null
}): Promise<Department> {
  return dropLabelCachesAfter(api().createDepartment(record))
}

export function updateDepartment(
  departmentId: string,
  changes: { department_name?: string; search_terms?: string | null; is_active?: boolean; row_version: number },
): Promise<Department> {
  return dropLabelCachesAfter(api().updateDepartment(departmentId, changes))
}

/** An Account authored on its own — `createDepartment`'s twin. */
export function createAccount(record: {
  account_id: string
  account_name: string
  search_terms?: string | null
}): Promise<Account> {
  return dropLabelCachesAfter(api().createAccount(record))
}

export function updateAccount(
  accountId: string,
  changes: {
    account_name?: string
    search_terms?: string | null
    is_active?: boolean
    row_version: number
  },
): Promise<Account> {
  return dropLabelCachesAfter(api().updateAccount(accountId, changes))
}

/**
 * What goes Inactive with an Account, a Department (owner, 2026-09-25 —
 * D-175) or a Code (D-180): the Active Codes, any derived half left with no
 * Active Code, and the Coding Rules naming them. The server's answer, read before the
 * confirmation opens; the PATCH then does exactly this in one transaction.
 */
export function previewInactiveCascade(kind: 'account' | 'department' | 'code', id: string): Promise<InactiveCascade> {
  return api().previewInactive(kind, id)
}

/**
 * How constrained a DEPARTMENT-ONLY vendor default is: it applies only where
 * it composes a real code with whatever account resolves. Computed
 * over `loadAllCodes()` — the WHOLE chart, not one 200-row page — because a
 * department whose codes happen to sort past the first page reached 0 of
 * 88 active accounts before this existed, which told the owner a legal rule was
 * worthless.
 */
export async function loadDepartmentReach(departmentId: string): Promise<{ fits: number; total: number }> {
  const [accounts, codes] = await Promise.all([loadAccounts(), loadAllCodes()])
  const fits = new Set(
    codes
      .filter((c) => c.is_active !== false)
      .map((c) => decomposeCodeId(c.code_id))
      .filter((halves): halves is NonNullable<typeof halves> => halves !== null && halves.departmentId === departmentId)
      .map((halves) => halves.accountId),
  )
  return { fits: fits.size, total: accounts.length }
}

/**
 * EVERY RULES LIST READS EVERY PAGE (code review D-01, 2026-10-17): coding
 * rules, routing rules, vendor aliases, company aliases and guidance. Each
 * made one request at the server's default page of 50, so a row past the 50th
 * could not be seen, searched or edited — and Memory (D-367) passes 50 within
 * days. The same fix D-357 made for Assignments.
 */
export function loadCodingRules(
  params: {
    vendor_id?: string
    /** G1: true = only the general rules, false = only the vendor-specific ones. */
    any_vendor?: boolean
    account_id?: string
    department_id?: string
  } = {},
): Promise<CodingRule[]> {
  return collectPages((cursor) => api().listCodingRules({ ...params, limit: MASTER_PAGE_LIMIT, cursor }))
}

/**
 * A rule naming BOTH halves must compose a real code; the server refuses one
 * that does not, so this passes the halves through rather than pre-checking —
 * a pre-flight check the server then re-checks is a race.
 */
export function createCodingRule(record: {
  /** Null makes a GENERAL rule (G1), which then requires a description scope. */
  vendor_id: string | null
  description_scope?: string | null
  account_id?: string | null
  department_id?: string | null
}): Promise<CodingRule> {
  return api().createCodingRule(record)
}

/**
 * A CODING RULE EDIT SENDS ONLY WHAT CHANGED (QA wave B, 2026-09-25, B-02;
 * `docs/decisions.md` D-168). It sent every field every time, so a Status-only
 * save arrived at `PATCH /coding-rules/{id}` looking like a change of both
 * halves, and the server checked the pair against the ACTIVE chart — refusing
 * to switch off a rule whose code had gone Inactive. The server no longer
 * checks a rule saved as Inactive at all; this keeps a save from claiming a
 * half changed when it did not, which is what decides how strictly the pair is
 * checked on a rule that stays Active.
 */
export function codingRuleChanges(
  existing: Pick<CodingRule, 'vendor_id' | 'description_scope' | 'account_id' | 'department_id' | 'is_active' | 'row_version'>,
  next: {
    vendor_id: string | null
    description_scope: string | null
    account_id: string | null
    department_id: string | null
  },
  isActive: boolean,
): {
  vendor_id?: string | null
  description_scope?: string | null
  account_id?: string | null
  department_id?: string | null
  is_active?: boolean
  row_version: number
} {
  return {
    ...(next.vendor_id !== (existing.vendor_id ?? null) ? { vendor_id: next.vendor_id } : {}),
    ...(next.description_scope !== (existing.description_scope ?? null)
      ? { description_scope: next.description_scope }
      : {}),
    ...(next.account_id !== (existing.account_id ?? null) ? { account_id: next.account_id } : {}),
    ...(next.department_id !== (existing.department_id ?? null) ? { department_id: next.department_id } : {}),
    ...(isActive !== existing.is_active ? { is_active: isActive } : {}),
    row_version: existing.row_version,
  }
}

/**
 * WHETHER AN EDIT CHANGED NOTHING (QA wave B review, B-02 and B-05). True when
 * every field the editor would send equals what the row already holds, with
 * `null`, `undefined`, absent and the empty string read as one. `row_version` is
 * ignored. Every References and Rules editor asks this before writing, and on
 * true closes with `NOTHING_CHANGED` rather than sending a PATCH that changes
 * nothing.
 *
 * THE EMPTY STRING IS BLANK (second review of QA wave B, B-15). The editors
 * send a blank box as `null` (`trim() || null`), so a row holding `''` in a
 * contact field — the mock's vendors held `phone: ''` for every row — read as
 * changed on every Save, and References → Vendors said "The vendor was saved."
 * for a Save that changed nothing. Both are blank on screen.
 */
export function nothingChanged(existing: object, next: Record<string, unknown>): boolean {
  const before = existing as Record<string, unknown>
  const blank = (v: unknown) => (v === undefined || v === '' ? null : v)
  return Object.entries(next).every(([key, value]) => key === 'row_version' || blank(before[key]) === blank(value))
}

// ---- Line groups and Rules -> Grouping (owner, 2026-10-22 — D-399, D-402) ----

/**
 * One change to an invoice's groups past the line editor (`POST
 * /invoices/{id}/line-groups`): at Controller Approval and Export Control,
 * where grouping may still change (G-12). Throws the server's refusal.
 */
export function changeLineGroups(invoiceId: string, rowVersion: number, operation: LineGroupOperation): Promise<LineGroupsResult> {
  return api().changeLineGroups(invoiceId, { row_version: rowVersion, operation })
}

/** A vendor's established groups, then Any vendor's (`vendorId` null: Any vendor's alone). */
export function loadLineGroups(vendorId: string | null): Promise<LineGroup[]> {
  return api().listLineGroups(vendorId)
}

export function loadGroupingRules(): Promise<GroupingRule[]> {
  return collectPages((cursor) => api().listGroupingRules({ limit: MASTER_PAGE_LIMIT, cursor }))
}

export function createGroupingRule(body: GroupingRuleCreateRequest): Promise<GroupingRule> {
  return api().createGroupingRule(body)
}

export function updateGroupingRule(id: string, changes: GroupingRulePatchRequest): Promise<GroupingRule> {
  return api().updateGroupingRule(id, changes)
}

export function updateCodingRule(
  id: string,
  changes: {
    /** Null turns a vendor rule into a general one (G1). */
    vendor_id?: string | null
    description_scope?: string | null
    account_id?: string | null
    department_id?: string | null
    is_active?: boolean
    row_version: number
  },
): Promise<CodingRule> {
  return api().updateCodingRule(id, changes)
}

/**
 * A rule reading "when an invoice arrives from `alias_name`, attribute it to
 * this vendor" (owner item 10b, 2026-08-13). The server normalizes and never
 * trusts the client's own trimming, and always writes `source: 'Manual'`.
 */
// --- Routing rules (Rules -> Routing; owner 2026-09-15, and the Controller
// list since 2026-10-07 — D-304) ----------------------------------------------

export function loadOversightRules(params: { active?: boolean } = {}): Promise<OversightRule[]> {
  return collectPages((cursor) => api().listOversightRules({ ...params, limit: MASTER_PAGE_LIMIT, cursor }))
}

/** A routing rule: its conditions and the approvals a match requires (D-336). */
export function createOversightRule(
  body: RequiredApprovals & { conditions: OversightRuleConditionWrite[] },
): Promise<OversightRule> {
  return api().createOversightRule(body)
}

export function updateOversightRule(
  id: string,
  body: Partial<RequiredApprovals> & {
    conditions?: OversightRuleConditionWrite[]
    is_active?: boolean
    row_version: number
  },
): Promise<OversightRule> {
  return api().updateOversightRule(id, body)
}

// --- Upload notifications (Rules -> Upload Notifications, owner 2026-10-07, D-306)

/** The notifications sent to the signed-in person, newest first (D-310). */
export function loadNotifications(): Promise<NotificationSummary[]> {
  return api().listNotifications()
}

/** One notification, whole: the message as it was emailed (D-310). */
export function loadNotification(id: string): Promise<Notification> {
  return api().getNotification(id)
}

export function loadUploadNotificationRecipients(
  params: { active?: boolean } = {},
): Promise<UploadNotificationRecipient[]> {
  return api().listUploadNotificationRecipients(params)
}

export function addUploadNotificationRecipient(
  body: UploadNotificationRecipientCreate,
): Promise<UploadNotificationRecipient> {
  return api().createUploadNotificationRecipient(body)
}

/** Removes the person from the company's list; the row is kept as Inactive. */
export function removeUploadNotificationRecipient(
  row: UploadNotificationRecipient,
): Promise<UploadNotificationRecipient> {
  return api().updateUploadNotificationRecipient(row.id, { is_active: false, row_version: row.row_version })
}

export function loadVendorAliases(params: { vendor_id?: string; active?: boolean } = {}): Promise<VendorAlias[]> {
  return collectPages((cursor) => api().listVendorAliases({ ...params, limit: MASTER_PAGE_LIMIT, cursor }))
}

export function createVendorAlias(record: {
  alias_name: string
  vendor_id: string
  evidence?: string | null
}): Promise<VendorAlias> {
  return api().createVendorAlias(record)
}

export function updateVendorAlias(
  id: string,
  changes: {
    alias_name?: string
    vendor_id?: string
    evidence?: string | null
    is_active?: boolean
    row_version: number
  },
): Promise<VendorAlias> {
  return api().updateVendorAlias(id, changes)
}

/**
 * A rule reading "when an invoice is billed to `alias_name`, it is this
 * company" (owner item 18, 2026-08-31 — Rules → Companies). The server
 * normalizes the text and refuses an alias carrying fewer than three letters
 * or digits.
 */
export function loadCompanyAliases(
  params: { company_id?: string; active?: boolean } = {},
): Promise<CompanyAlias[]> {
  return collectPages((cursor) => api().listCompanyAliases({ ...params, limit: MASTER_PAGE_LIMIT, cursor }))
}

export function createCompanyAlias(record: {
  alias_name: string
  company_id: string
  evidence?: string | null
}): Promise<CompanyAlias> {
  return api().createCompanyAlias(record)
}

export function updateCompanyAlias(
  id: string,
  changes: {
    alias_name?: string
    company_id?: string
    evidence?: string | null
    is_active?: boolean
    row_version: number
  },
): Promise<CompanyAlias> {
  return api().updateCompanyAlias(id, changes)
}

/**
 * Rules -> Guidance: standing pass-1 extraction guidance, authored either by
 * the nightly training-loop worker from a repeated correction pattern
 * (docs/ai.md §9) or by hand on the tab (M6, 2026-09-11). A human may create,
 * supersede (never an in-place edit) or retire/reactivate.
 */
export function loadVendorExtractionGuidance(
  params: { q?: string; active?: boolean } = {},
): Promise<VendorExtractionGuidance[]> {
  return collectPages((cursor) =>
    api().listVendorExtractionGuidance({ ...params, limit: MASTER_PAGE_LIMIT, cursor }),
  )
}

/**
 * Guidance written by hand — a vendor and a sentence, and the server fills in
 * the rest (M6). Where the vendor already has active guidance this REPLACES
 * it as a supersede (M7): the old version is retired into the audit chain,
 * never overwritten, and what comes back is the new version.
 */
export function createVendorExtractionGuidance(record: {
  vendor_id: string
  instruction: string
}): Promise<VendorExtractionGuidance> {
  return api().createVendorExtractionGuidance(record)
}

export function supersedeVendorExtractionGuidance(
  id: string,
  changes: { instruction: string; row_version: number },
): Promise<VendorExtractionGuidance> {
  return api().supersedeVendorExtractionGuidance(id, changes)
}

export function updateVendorExtractionGuidanceActive(
  id: string,
  changes: { is_active: boolean; row_version: number },
): Promise<VendorExtractionGuidance> {
  return api().updateVendorExtractionGuidanceActive(id, changes)
}

// ---- The Marketing category vocabulary (owner item 11, 2026-08-19) —
// References' manager, same list/create/patch/soft-retire shape as
// `VehiclesManager` below. The vocabulary is small (three seeded rows), so the management read
// takes no `active` filter — the manager itself renders retired rows behind
// its own "Show retired" switch, same as every other master manager.

export async function loadMarketingCategoriesForManagement(term?: string): Promise<MarketingCategory[]> {
  return api().listMarketingCategories({ q: term || undefined, limit: MASTER_PAGE_LIMIT })
}

export function createMarketingCategory(body: { category: string; context: string }): Promise<MarketingCategory> {
  return api().createMarketingCategory(body)
}

export function updateMarketingCategory(
  id: string,
  changes: { category?: string; context?: string; is_active?: boolean; row_version: number },
): Promise<MarketingCategory> {
  return api().updateMarketingCategory(id, changes)
}

export async function loadVehiclesForManagement(term?: string): Promise<Vehicle[]> {
  const page = await api().listVehicles({ q: term || undefined, limit: 200 })
  return page.data
}

export function createVehicle(record: Partial<Vehicle> & { number: string }): Promise<Vehicle> {
  return api().createVehicle(record)
}

export function updateVehicle(
  vehicleId: string,
  changes: Partial<Vehicle> & { row_version: number },
): Promise<Vehicle> {
  return api().updateVehicle(vehicleId, changes)
}

/** The fleet spreadsheet is parsed on the server, so preview and commit cannot
    disagree about what the file said, and the browser never interprets a file.
    Absent vehicles are reported and never changed — a filtered export must not
    be able to retire half the fleet. */
export function previewVehicleImport(file: File): Promise<VehicleImportPreview> {
  return api().previewVehicleImport(file)
}

export function commitVehicleImport(previewToken: string): Promise<VehicleImportResult> {
  return api().commitVehicleImport(previewToken)
}

// ---- Analytics --------------------------------------------------------------
// Aggregates, computed by the API and scoped to what the caller may see. The
// old dashboard pulled every active invoice, decision, flag and correction into
// the browser to draw its bars.

export function loadAnalyticsSpendByEntity(params?: AnalyticsParams): Promise<AnalyticsSpendByEntity> {
  return api().getAnalyticsSpendByEntity(params)
}

export function loadAnalyticsCorrections(params?: AnalyticsParams): Promise<AnalyticsCorrections> {
  return api().getAnalyticsCorrections(params)
}

export function loadAnalyticsByDimension(
  dimension: NonNullable<AnalyticsParams['dimension']>,
  params?: AnalyticsParams,
): Promise<AnalyticsByDimension> {
  return api().getAnalyticsByDimension(dimension, params)
}

export function loadAnalyticsSpendByCode(
  groupBy: NonNullable<AnalyticsParams['group_by']>,
  params?: AnalyticsParams,
): Promise<AnalyticsSpendByCode> {
  return api().getAnalyticsSpendByCode(groupBy, params)
}

export function loadAnalyticsKpis(): Promise<AnalyticsKpis> {
  return api().getAnalyticsKpis()
}

// ---- Queue counts -----------------------------------------------------------

// Re-exported so `Timeline.tsx` — the domain layer's consumer, not the
// transport's — imports these from `lib/` rather than `api/types` directly.
export type { StuckNode, StuckNodeCompany }

/** The Pipeline dialog's warning-icon diagnostic (§2.2). Fetched only when the
    dialog is open, never on the 30-second refresh — see `docs/api.md` §2.2 for why
    this is a separate endpoint from the badge counts above. A tier with
    nothing stuck is absent from the result, not zeroed. */
export async function loadStuckNodes(): Promise<Partial<Record<string, StuckNode>>> {
  const res = await api().getStuckQueues({ workspace: 'invoice' })
  return res.stuck
}
